import {FormattedMessage, injectIntl} from '@meiko/react-intl';
import Button from 'components/ui/Button';
import Collapse from 'components/generic/Collapse';
import useWindowWidth from 'hooks/windowWidth';
import React, {memo, useState, useMemo, useCallback} from 'react';
import {screenMdMinValue} from 'styles/constants';
import {RANGE_OPTIONS, rangeToDates} from 'utils/formData';
import {twMerge} from 'tailwind-merge';
import {useAuth} from 'hooks/useAuth';
import {useTeams} from 'data/team';
import {useUsers} from 'data/user';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import MultiSelect from 'components/ui/MultiSelect';
import Input from 'components/ui/form/Input';
import {Form, FormField as Field} from 'components/ui/form/Form';
import {formatPersonData, formatTeamData, formatRelatedData} from 'utils/dataFormatters';

const createFormSchema = (canClearTeam = false) =>
	z.object({
		organizationId: z.array(z.number()).min(1, 'Required'),
		teamId: canClearTeam ? z.array(z.number()).optional() : z.array(z.number()),
		userId: z.array(z.number()),
		startsAt: z.string().min(1, 'Required'),
		endsAt: z.string().min(1, 'Required'),
	});

const DashboardFilterForm = memo(
	({
		intl,
		initialValues = {},
		onSubmit,
		canChangeUser,
		canChangeTeam,
		canClearTeam,
		canChangeOrganization,
	}) => {
		const {width: windowWidth} = useWindowWidth();
		const {user} = useAuth();

		const [isVisible, setIsVisible] = useState(false);

		const organizations = user?.organizations || [];

		// Memoize schema to prevent recreation on every render
		const formSchema = useMemo(() => createFormSchema(canClearTeam), [canClearTeam]);

		const form = useForm({
			resolver: zodResolver(formSchema),
			defaultValues: useMemo(
				() => ({
					organizationId: initialValues?.organizationId || [],
					teamId: initialValues?.teamId || [],
					userId: initialValues?.userId || [],
					startsAt: initialValues?.startsAt || '',
					endsAt: initialValues?.endsAt || '',
				}),
				[initialValues],
			),
		});

		const organizationIds = form.watch('organizationId');
		const teamIds = form.watch('teamId');
		const currentStartsAt = form.watch('startsAt');
		const currentEndsAt = form.watch('endsAt');

		const {data: teamsDataRaw = []} = useTeams({
			params: {
				organizationId:
					organizationIds.length > 0
						? organizationIds
						: [user?.organizations?.[0]?.id].filter(Boolean),
				isCalendar: true,
				type: 'sales',
				getAllTeams: true,
				include: ['users'],
			},
		});

		// Teams use default unwrap, so data is directly the array
		const teamsData = teamsDataRaw || [];

		const {data: usersDataRaw} = useUsers({
			params: {
				teamId: teamIds.length > 0 ? teamIds : undefined,
			},
		});

		// Users resource has custom unwrap: x => x, so data is nested
		const usersData = usersDataRaw?.data || [];

		const setDateRange = useCallback(
			range => {
				const [start, end] = rangeToDates(range);
				form.setValue('startsAt', start);
				form.setValue('endsAt', end);
			},
			[form],
		);

		const handleFormSubmit = useCallback(
			form.handleSubmit(data => {
				onSubmit(data);
			}),
			[form, onSubmit],
		);

		const shouldBeBehindToggle = windowWidth < screenMdMinValue;

		// Memoize active range determination
		const activeRange = useMemo(() => {
			if (!currentStartsAt || !currentEndsAt) return null;
			for (const {value} of RANGE_OPTIONS) {
				const [start, end] = rangeToDates(value);
				if (start === currentStartsAt && end === currentEndsAt) {
					return value;
				}
			}
			return null;
		}, [currentStartsAt, currentEndsAt]);

		// Memoize options to prevent recreation on every render
		const organizationOptions = useMemo(
			() =>
				organizations.map(org => ({
					label: formatRelatedData(org, 'title'),
					value: org.id,
				})),
			[organizations],
		);

		const teamOptions = useMemo(
			() =>
				teamsData.map(team => ({
					label: formatTeamData(team),
					value: team.id,
				})),
			[teamsData],
		);

		const userOptions = useMemo(
			() =>
				usersData.map(user => ({
					label: formatPersonData(user),
					value: user.id,
				})),
			[usersData],
		);

		// Memoize onChange handlers to prevent unnecessary re-renders
		const handleOrganizationChange = useCallback(
			value => {
				// Clear teams and users when org changes
				form.setValue('teamId', []);
				form.setValue('userId', []);
			},
			[form],
		);

		const handleTeamChange = useCallback(
			value => {
				// Clear users when teams change
				form.setValue('userId', []);
			},
			[form],
		);

		return (
			<Collapse
				showToggle={shouldBeBehindToggle}
				isVisible={shouldBeBehindToggle ? isVisible : true}
				onToggle={value => setIsVisible(value)}
				title={intl.formatMessage({id: 'Filter'})}
			>
				<div className={twMerge('mt-5 md:mt-0')}>
					<Form
						form={form}
						schema={formSchema}
						onSubmit={handleFormSubmit}
						className="gap-4"
					>
						<div className={twMerge('flex flex-col-reverse gap-2.5 md:flex-row')}>
							<div className="inline-flex overflow-hidden rounded-md [&>*:not(:first-child)]:border-l [&>*:not(:first-child)]:border-gray-200 [&>*:first-child]:rounded-l-md [&>*:last-child]:rounded-r-md [&>*]:rounded-none">
								{RANGE_OPTIONS.map(({label, value}) => (
									<Button
										key={value}
										variant={activeRange === value ? 'dark' : 'default'}
										onClick={() => setDateRange(value)}
									>
										<FormattedMessage id={label} />
									</Button>
								))}
							</div>

							<Button variant="green" type="submit">
								<FormattedMessage id="Search" />
							</Button>
						</div>

						<div className={twMerge('grid gap-2.5 mt-2.5 md:grid-cols-5')}>
							<Field name="startsAt" label={<FormattedMessage id="Starts at" />}>
								<Input type="date" />
							</Field>

							<Field name="endsAt" label={<FormattedMessage id="Ends at" />}>
								<Input type="date" />
							</Field>

							<Field
								name="organizationId"
								label={<FormattedMessage id="Organization" />}
								required
							>
								<MultiSelect
									options={organizationOptions}
									disabled={!canChangeOrganization}
									onChange={handleOrganizationChange}
								/>
							</Field>

							<Field name="teamId" label={<FormattedMessage id="Teams" />}>
								<MultiSelect
									options={teamOptions}
									disabled={!canChangeTeam}
									onChange={handleTeamChange}
								/>
							</Field>

							<Field name="userId" label={<FormattedMessage id="Users" />}>
								<MultiSelect options={userOptions} disabled={!canChangeUser} />
							</Field>
						</div>
					</Form>
				</div>
			</Collapse>
		);
	},
);

DashboardFilterForm.displayName = 'DashboardFilterForm';

export default injectIntl(DashboardFilterForm);
